import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Table, Alert, Spin, Button, Modal, Form, Input, message, Tag, Select, Row, Col, Empty } from 'antd';
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getAllMessages, deleteMessage, replyMessage } from '../../store/features/dashboard/messagesSlice';
import { debounce } from '../../lib/utils';
const { Option } = Select;

const Messages = () => {
  const dispatch = useDispatch();
  const { 
    messages, 
    total, 
    loading, 
    error,
    currentPage,
    pageSize,
  } = useSelector((state) => state.message);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isSearching, setIsSearching] = useState(false);
  const [form] = Form.useForm();
  const searchInputRef = useRef(null);

  const fetchMessages = useCallback((page, size, search, status, active) => {
    setIsSearching(true);
    dispatch(getAllMessages({ 
      page, 
      limit: size,
      search,
      status: status !== 'all' ? status : undefined,
      active: active !== 'all' ? active : undefined
    })).finally(() => {
      setIsSearching(false);
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });
  }, [dispatch]);

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      if (searchValue.length >= 2 || searchValue.length === 0) {
        fetchMessages(currentPage, pageSize, searchValue, statusFilter, activeFilter);
      }
    }, 500),
    [fetchMessages, currentPage, pageSize, statusFilter, activeFilter]
  );

  useEffect(() => {
    fetchMessages(currentPage, pageSize, searchText, statusFilter, activeFilter);
  }, [fetchMessages, currentPage, pageSize, statusFilter, activeFilter]);

  const handleSearch = (value, event) => {
    setSearchText(value);
    if (event && event.type === 'change') {
      debouncedSearch(value);
    } else {
      fetchMessages(currentPage, pageSize, value, statusFilter, activeFilter);
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
    setStatusFilter('all');
    setActiveFilter('all');
    fetchMessages(currentPage, pageSize, '', 'all', 'all');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    fetchMessages(currentPage, pageSize, searchText, value, activeFilter);
  };

  const handleActiveFilterChange = (value) => {
    setActiveFilter(value);
    fetchMessages(currentPage, pageSize, searchText, statusFilter, value);
  };

  const handlePageChange = (page, size) => {
    fetchMessages(page, size, searchText, statusFilter, activeFilter);
  };

  const handleDelete = (messageId) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this message?',
      content: 'This will soft delete the message',
      okText: 'Yes, delete',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        dispatch(deleteMessage(messageId))
          .unwrap()
          .then(() => {
            message.success('Message deleted successfully');
            fetchMessages(currentPage, pageSize, searchText, statusFilter, activeFilter);
          })
          .catch((err) => {
            message.error(err || 'Failed to delete message');
          });
      },
    });
  };

  const handleReply = (message) => {
    setSelectedMessage(message);
    form.setFieldsValue({
      email: message.user.email,
      replyMessage: '',
    });
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    form.validateFields()
      .then((values) => {
        const replyData = {
          messageId: selectedMessage.id,
          replyMessage: values.replyMessage,
        };
        
        dispatch(replyMessage(replyData))
          .unwrap()
          .then(() => {
            message.success('Reply sent successfully');
            setIsModalVisible(false);
            form.resetFields();
            fetchMessages(currentPage, pageSize, searchText, statusFilter, activeFilter);
          })
          .catch((err) => {
            message.error(err || 'Failed to send reply');
          });
      });
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      fixed: 'left',
      render: (status) => (
        <Tag color={status === 'replied' ? 'green' : 'red'}>
          {status === 'replied' ? 'Replied' : 'Unreplied'}
        </Tag>
      ),
    },
    {
      title: 'Message Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => (
        <div>
          {new Date(date).toLocaleString()}
        </div>
      ),
    },
    {
      title: 'Active Status',
      dataIndex: 'active_status',
      key: 'active_status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'active' ? 'blue' : 'orange'}>
          {status === 'active' ? 'Active' : 'Deleted'}
        </Tag>
      ),
    },
    {
      title: 'Name',
      dataIndex: ['user', 'name'],
      key: 'name',
      width: 150,
    },
    {
      title: 'Email',
      dataIndex: ['user', 'email'],
      key: 'email',
      width: 200,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      width: 300,
      render: (text) => (
        <div style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '300px'
        }}>
          {text}
        </div>
      ),
    },
    {
      title: 'Replies',
      dataIndex: 'replies',
      key: 'replies',
      width: 300,
      render: (replies) => (
        <div style={{
          whiteSpace: 'normal',
          maxWidth: '300px'
        }}>
          {replies.length > 0 
            ? replies.map((reply, index) => (
                <div key={index} style={{ marginBottom: '8px' }}>
                  <strong>{new Date(reply.created_at).toLocaleString()}:</strong> {reply.message}
                </div>
              ))
            : 'No replies yet'
          }
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button 
            type="primary" 
            onClick={() => handleReply(record)}
            disabled={record.active_status === 'deleted'}
          >
            Reply
          </Button>
          <Button 
            type="primary" 
            danger 
            onClick={() => handleDelete(record.id)}
            disabled={record.active_status === 'deleted'}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  if (loading && messages.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Messages"
        description={error}
        type="error"
        showIcon
        style={{ margin: '24px' }}
      />
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <h2 style={{ marginBottom: '16px' }}>Messages Management</h2>
      
      <div style={{ marginBottom: '16px' }}>
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={8}>
            <Select
              allowClear
              value={statusFilter}
              style={{ width: '100%' }}
              onChange={handleStatusFilterChange}
              placeholder="Filter by reply status"
            >
              <Option value="all">All Status</Option>
              <Option value="replied">Replied</Option>
              <Option value="unreplied">Unreplied</Option>
            </Select>
          </Col>
          <Col span={8}>
            <Select
              allowClear
              value={activeFilter}
              style={{ width: '100%' }}
              onChange={handleActiveFilterChange}
              placeholder="Filter by active status"
            >
              <Option value="all">All Active Status</Option>
              <Option value="active">Active</Option>
              <Option value="deleted">Deleted</Option>
            </Select>
          </Col>
          <Col span={8}>
            <Input.Search
              ref={searchInputRef}
              placeholder="Search messages..."
              allowClear
              enterButton={isSearching ? <LoadingOutlined /> : <SearchOutlined />}
              size="large"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value, e)}
              onSearch={(value) => handleSearch(value, { type: 'search' })}
              onClear={handleClearSearch}
              loading={isSearching}
            />
          </Col>
        </Row>
      </div>

      {messages.length === 0 && !loading ? (
        <Empty
          description={
            searchText || statusFilter !== 'all' || activeFilter !== 'all'
              ? `No messages found matching your criteria` 
              : 'No messages available'
          }
          style={{ margin: '40px 0' }}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={messages}
          loading={loading}
          rowKey="id"
          bordered
          scroll={{ x: 'max-content', y: 'calc(100vh - 340px)' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '15', '20'],
            showTotal: (total, range) => (
              <span>
                Showing {range[0]}-{range[1]} of {total} records
              </span>
            ),
            position: ['bottomCenter'],
          }}
        />
      )}

      <Modal
        title="Reply to Message"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        okText="Send Reply"
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="email" label="Email">
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="replyMessage"
            label="Reply Message"
            rules={[{ required: true, message: 'Please enter your reply message' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Messages;